/*
 * <cd-marquee> — keeps the press strip wide enough to fill the band.
 *
 * The loop works by translating the track -50%, which is only seamless when the
 * track is exactly two identical halves. Liquid renders two copies of the logo
 * set, which is correct but not necessarily *wide* enough: once the track
 * slides a full half-width, anything past its right edge is bare Predawn. With
 * four mastheads that gap opens up on any screen wider than about 1400px.
 *
 * So: measure one set, work out how many it takes to cover the band, and clone
 * up to twice that. The track stays two equal halves, and each half on its own
 * covers the viewport — so there is never a gap, at any width.
 *
 * Clones are aria-hidden; the first set is the only one a screen reader sees.
 *
 * Wrapped in an IIFE because a second instance of this section on one
 * template loads this file with its own <script src>, so the browser
 * executes it twice; without the wrapper the second execution throws on the
 * repeated top-level `class` declaration before the customElements.get()
 * guard below ever runs.
 */
(function () {
class CdMarquee extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('.cd-marquee__viewport');
    this.track = this.querySelector('.cd-marquee__track');
    if (!this.viewport || !this.track) return;

    this.template = this.track.firstElementChild;
    if (!this.template) return;

    this.fit = this.fit.bind(this);

    // Belt and braces on the resize signal. ResizeObserver is the precise one,
    // but it does not fire in every environment (emulated viewports, notably),
    // and a marquee that runs out of logos after a window resize is worse than
    // a couple of redundant measurements. fit() early-returns when nothing has
    // changed, so listening to all three is cheap.
    this.observer = new ResizeObserver(this.fit);
    this.observer.observe(this.viewport);
    window.addEventListener('resize', this.fit);
    window.addEventListener('orientationchange', this.fit);

    // Logo widths are unknown until the images decode, so re-fit as they land.
    this.querySelectorAll('img').forEach((img) => {
      if (!img.complete) img.addEventListener('load', this.fit, { once: true });
    });

    this.fit();
  }

  disconnectedCallback() {
    this.observer?.disconnect();
    window.removeEventListener('resize', this.fit);
    window.removeEventListener('orientationchange', this.fit);
  }

  fit() {
    const band = this.viewport.clientWidth;
    const unit = this.template.getBoundingClientRect().width;
    if (!band || !unit) return;

    // Sets per half, then double so the track remains two equal halves.
    const perHalf = Math.max(1, Math.ceil(band / unit));
    const want = perHalf * 2;
    const have = this.track.children.length;
    if (have === want) return;

    if (have < want) {
      const batch = document.createDocumentFragment();
      for (let i = have; i < want; i += 1) {
        const clone = this.template.cloneNode(true);
        clone.setAttribute('aria-hidden', 'true');
        batch.appendChild(clone);
      }
      this.track.appendChild(batch);
    } else {
      // Never drop below the two halves the markup ships with.
      for (let i = have; i > Math.max(want, 2); i -= 1) {
        this.track.lastElementChild.remove();
      }
    }
  }
}

if (!customElements.get('cd-marquee')) {
  customElements.define('cd-marquee', CdMarquee);
}
})();
